import React, { useEffect, useState } from "react";
import { Grid, Cell, SelectionControl } from "react-md";

interface MCheckboxGroupProps {
  // Note that values are set in useEffect. If you're going to pass a func to get the options,
  // be sure to memoize it so it doesn't get called every render and reset the selections
  fields: MCheckboxGroupOption[]; 
  cellSize?: number;
  containerClassName?: string;
  gridClassname?: string;
  controlMethods?: (methods: MCheckboxGroupControlMethods) => void;
  onChange?: (value: string, checked: boolean) => void;
}

export interface MCheckboxGroupOption {
  value: string; // this is meant to be a code to identify the field, it isnt the value of if its checked or not
  label: string | React.ReactNode;
  defaultChecked?: boolean;
  disabled?: boolean;
  checked?: boolean; // used for controlled mode - parent owns the state
}

export interface MCheckboxGroupControlMethods {
  getValues: () => MCheckboxValue[];  // Get all checkbox values
  getChecked: () => string[]; // Get selected values
  getUnChecked: () => string[]; // Get unselected values
  checkAll: () => void; // Select all (uncontrolled mode only)
  unCheckAll: () => void; // Unselect all (uncontrolled mode only)
}

export interface MCheckboxValue {
  value: string;
  checked: boolean;
}

const MCheckboxGroup = (props: MCheckboxGroupProps) => {
  // Controlled mode: parent owns state via `checked` prop
  // Uncontrolled mode: component owns state, initialized from `defaultChecked`
  const isControlled = props.fields.some(f => f.checked !== undefined);

  const [options, setOptions] = useState<boolean[]>(props.fields.map(f => !!f.defaultChecked));

  const getCheckedState = (index: number): boolean => 
    isControlled ? !!props.fields[index].checked : options[index];

  useEffect(() => {
    if (props.controlMethods) {
      props.controlMethods({
        getValues: () => props.fields.map((f, i) => ({ value: f.value, checked: getCheckedState(i) })),
        getChecked: () => props.fields.filter((_, i) => getCheckedState(i)).map(f => f.value),
        getUnChecked: () => props.fields.filter((_, i) => !getCheckedState(i)).map(f => f.value),
        checkAll: () => {
          if (!isControlled) setOptions(props.fields.map(() => true));
        },
        unCheckAll: () => {
          if (!isControlled) setOptions(props.fields.map(() => false));
        },
      });
    }
  });

  useEffect(() => {
    if (!isControlled) {
      setOptions(props.fields.map(f => !!f.defaultChecked));
    }
  }, [props.fields, isControlled]);

  const onChange = (index: number) => {
    if (isControlled) {
      // Controlled: just notify parent, don't update internal state
      props.onChange?.(props.fields[index].value, !props.fields[index].checked);
    } else {
      // Uncontrolled: update internal state
      const newOptions = [...options];
      newOptions[index] = !newOptions[index];
      setOptions(newOptions);
      props.onChange?.(props.fields[index].value, newOptions[index]);
    }
  };

  return (
    <Grid className={`m-checkbox-group ${props.gridClassname || ''}`}>
        {props.fields.map(({ label, value, defaultChecked, disabled }, index) =>
          <Cell key={`${value}-${index}`} size={props.cellSize}>
            <SelectionControl
              {...{ label, defaultChecked, disabled }}
              key={`checkbox-${index}`}
              checked={getCheckedState(index)}
              onChange={() => onChange(index)}
              type="checkbox"
              name={value}
              id={value}
              className={props.containerClassName}
            />
          </Cell>
        )}
      </Grid>
  );
};

export default MCheckboxGroup;